import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2, Search, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCancelScheduledDrop } from '~entities/scheduled-drops/api/cancel-scheduled-drop';
import {
  type ScheduledDropFilters,
  type ScheduledDropResponse,
  useGetScheduledDrops,
} from '~entities/scheduled-drops/api/get-scheduled-drops';

import { InputDatePicker } from '~shared/components/input-date-picker';
import { INPUT_DATE_PICKER_ICON_POSITION } from '~shared/components/input-date-picker.types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import { isScheduledDropDateRangeInvalid } from './scheduled-drops.helpers';
import { ScheduledDropsTable } from './scheduled-drops-table';
import { SCHEDULED_DROP_STATUS } from './scheduled-drops.types';

export function ScheduledDropsPage() {
  const [filters, setFilters] = useState<ScheduledDropFilters>({});
  const [studentNameSearch, setStudentNameSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [selectedDrop, setSelectedDrop] = useState<ScheduledDropResponse | null>(null);

  const { data: scheduledDrops, isLoading, refetch } = useGetScheduledDrops(filters);
  const cancelMutation = useCancelScheduledDrop();

  const isDateRangeInvalid = useMemo(
    () => isScheduledDropDateRangeInvalid(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  const handleApplyFilters = () => {
    if (isDateRangeInvalid) {
      return;
    }

    const newFilters: ScheduledDropFilters = {};

    if (studentNameSearch) newFilters.studentName = studentNameSearch;
    if (dateFrom) newFilters.scheduledDropDateFrom = dateFrom;
    if (dateTo) newFilters.scheduledDropDateTo = dateTo;
    if (
      statusFilter === SCHEDULED_DROP_STATUS.PENDING ||
      statusFilter === SCHEDULED_DROP_STATUS.EXECUTED ||
      statusFilter === SCHEDULED_DROP_STATUS.CANCELLED
    ) {
      newFilters.status = statusFilter;
    }

    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setStudentNameSearch('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
    setFilters({});
  };

  const handleViewReason = useCallback((drop: ScheduledDropResponse) => {
    setSelectedDrop(drop);
    setReasonModalOpen(true);
  }, []);

  const handleOpenCancelDialog = useCallback((drop: ScheduledDropResponse) => {
    setSelectedDrop(drop);
    setCancelDialogOpen(true);
  }, []);

  const handleCancelDrop = async () => {
    if (!selectedDrop) return;

    try {
      await cancelMutation.mutateAsync(selectedDrop.id);
      toast.success('Scheduled drop cancelled successfully');
      setCancelDialogOpen(false);
      setSelectedDrop(null);
      await refetch();
    } catch (error) {
      toast.error('Failed to cancel scheduled drop');
      console.error(error);
    }
  };

  return (
    <div className='min-h-screen bg-background p-4 md:p-8 lg:p-12'>
      <Link to='/dashboard' className='flex w-fit items-center gap-2 mb-8'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Scheduled Drops</h2>
      </Link>

      <Card className='mb-6'>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Filter scheduled drops by student, date range, and status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex flex-col gap-4'>
            <div className='space-y-2'>
              <label className='text-sm font-medium' htmlFor='studentName'>
                Student Name
              </label>
              <div className='relative'>
                <Search className='absolute left-2 top-2.5 size-4 text-muted-foreground' />
                <Input
                  id='studentName'
                  placeholder='Search by first or last name'
                  value={studentNameSearch}
                  onChange={(e) => setStudentNameSearch(e.target.value)}
                  className='pl-8'
                />
              </div>
            </div>

            <div className='grid grid-cols-2 gap-2'>
              <div className='min-w-0 space-y-2'>
                <label className='text-sm font-medium' htmlFor='dateFrom'>
                  Date From
                </label>
                <InputDatePicker
                  id='dateFrom'
                  value={dateFrom}
                  onChange={setDateFrom}
                  ariaLabel='Date from'
                  align='start'
                  displayFormat='MM/dd/yyyy'
                  iconPosition={INPUT_DATE_PICKER_ICON_POSITION.END}
                  className='h-8 px-2'
                />
              </div>
              <div className='min-w-0 space-y-2'>
                <label className='text-sm font-medium' htmlFor='dateTo'>
                  Date To
                </label>
                <InputDatePicker
                  id='dateTo'
                  value={dateTo}
                  onChange={setDateTo}
                  ariaLabel='Date to'
                  align='end'
                  displayFormat='MM/dd/yyyy'
                  iconPosition={INPUT_DATE_PICKER_ICON_POSITION.END}
                  className='h-8 px-2'
                />
              </div>
            </div>

            <div className='space-y-2'>
              <label className='text-sm font-medium'>Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='Select status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All</SelectItem>
                  <SelectItem value={SCHEDULED_DROP_STATUS.PENDING}>
                    Pending
                  </SelectItem>
                  <SelectItem value={SCHEDULED_DROP_STATUS.EXECUTED}>
                    Executed
                  </SelectItem>
                  <SelectItem value={SCHEDULED_DROP_STATUS.CANCELLED}>
                    Cancelled
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isDateRangeInvalid ? (
            <p className='mt-2 text-sm text-red-600'>
              Date from must be on or before date to.
            </p>
          ) : null}

          <div className='mt-4 flex gap-2'>
            <Button onClick={handleApplyFilters} disabled={isDateRangeInvalid}>
              <Search className='mr-2 size-4' />
              Apply Filters
            </Button>
            <Button variant='outline' onClick={handleClearFilters}>
              <X className='size-4 mr-2' />
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Scheduled Drops</CardTitle>
          <CardDescription>
            {isLoading ? 'Loading...' : `${scheduledDrops?.length ?? 0} scheduled drop(s) found`}
          </CardDescription>
        </CardHeader>
        <CardContent className='min-w-0'>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='size-8 animate-spin' />
            </div>
          ) : scheduledDrops && scheduledDrops.length > 0 ? (
            <ScheduledDropsTable
              drops={scheduledDrops}
              onViewReason={handleViewReason}
              onCancel={handleOpenCancelDialog}
            />
          ) : (
            <div className='py-8 text-center'>
              <p className='text-muted-foreground'>No scheduled drops found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Scheduled Drop</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this scheduled drop? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelDrop}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Cancelling...
                </>
              ) : (
                'Yes, cancel drop'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reason Modal */}
      <Dialog open={reasonModalOpen} onOpenChange={setReasonModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Drop Reason</DialogTitle>
            <DialogDescription>
              Reason provided for scheduling this drop
            </DialogDescription>
          </DialogHeader>
          <div className='py-4'>
            <div className='space-y-4'>
              {selectedDrop?.student && (
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>Student</p>
                  <p className='text-base'>
                    {selectedDrop.student.firstName} {selectedDrop.student.lastName}
                  </p>
                </div>
              )}
              {selectedDrop?.enrollment && (
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>Class</p>
                  <p className='text-base'>{selectedDrop.enrollment.name}</p>
                </div>
              )}
              <div>
                <p className='text-sm font-medium text-muted-foreground'>Reason</p>
                <p className='text-base whitespace-pre-wrap break-all'>{selectedDrop?.dropReason}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
